import React, { useMemo, useState, useRef, useEffect } from "react";
import { useDropzone, FileRejection, Accept, DropEvent } from "react-dropzone";
import { useMutation, gql } from "@apollo/client";
import classNames from "classnames";
import { DndProvider, useDrag, useDrop, DropTargetMonitor } from "react-dnd";
import { HTML5Backend } from "react-dnd-html5-backend";
import Button from "components/base/Button";
import imageIcon from "assets/img/icons/image-icon.png";
import CustomImageAttachmentPreview from "components/custom/CustomImageAttachmentPreview";

// ---------- (A) GraphQL mutation ----------
export const UPLOAD_FILES = gql`
  mutation UploadFiles($input: [UploadInput!]!) {
    UploadFiles(input: $input) {
      items {
        name
        full_path
      }
    }
  }
`;

// ---------- (B) Types ----------
interface FileAttachment {
  name: string;
  preview: string; // URL or local preview
  originalUrl?: string; // API URL (must be added)
  size: number; // e.g. in KB
  resolution: string; // 'width x height px'
  uploadProgress?: number;
  isUploading?: boolean;
  roles?: Record<string, boolean>;
}

interface CustomDropzoneProps {
  className?: string;
  size?: "sm";
  reactDropZoneProps?: any;
  accept?: Accept;
  noPreview?: boolean;
  onDrop?: (newPreviews: FileAttachment[]) => void;
  defaultFiles?: FileAttachment[];
  onFileRemove?: (fileName: string) => void;
  onFileUpdate?: (index: number, updatedData: Partial<FileAttachment>) => void;
  onFileIndexChange?: (fromIndex: number, toIndex: number) => void;
  children?: React.ReactNode;
}

// For dragging items
const ItemType = "IMAGE";
interface DragItem {
  index: number;
  id: string;
  type: string;
}

// ---------- (C) DraggableImage component ----------
const DraggableImage: React.FC<{
  id: string;
  index: number;
  moveImage: (dragIndex: number, hoverIndex: number) => void;
  children: React.ReactNode;
}> = ({ id, index, moveImage, children }) => {
  const ref = useRef<HTMLDivElement>(null);

  const [, drop] = useDrop<DragItem>({
    accept: ItemType,
    hover(item, monitor: DropTargetMonitor) {
      if (!ref.current || item.index === index) return;
      const hoverBoundingRect = ref.current.getBoundingClientRect();
      const clientOffset = monitor.getClientOffset();
      if (!clientOffset) return;

      const threshold = 20;
      const hoverClientY = clientOffset.y - hoverBoundingRect.top;
      const hoverClientX = clientOffset.x - hoverBoundingRect.left;
      const isWithinYThreshold =
        hoverClientY < threshold ||
        hoverClientY > hoverBoundingRect.height - threshold;
      const isWithinXThreshold =
        hoverClientX < threshold ||
        hoverClientX > hoverBoundingRect.width - threshold;

      if (isWithinYThreshold || isWithinXThreshold) {
        setTimeout(() => {
          moveImage(item.index, index);
          item.index = index;
        }, 100);
      }
    },
  });

  const [{ isDragging }, drag] = useDrag({
    type: ItemType,
    item: { id, index, type: ItemType },
    collect: (monitor) => ({
      isDragging: monitor.isDragging(),
    }),
  });

  drag(drop(ref));

  return (
    <div ref={ref} style={{ opacity: isDragging ? 0.5 : 1 }}>
      {children}
    </div>
  );
};

// ---------- (D) Main Dropzone component ----------
export default function CustomDropzone({
  className,
  size,
  onDrop,
  accept,
  noPreview,
  reactDropZoneProps,
  onFileRemove,
  onFileUpdate,
  onFileIndexChange,
  children,
  defaultFiles = [],
}: CustomDropzoneProps) {
  const [previews, setPreviews] = useState<FileAttachment[]>(defaultFiles);
  const [imageRoles, setImageRoles] = useState<{ [role: string]: number }>({});
  const baseUrl = process.env.REACT_APP_MEDIA_SERVER_API_URL;
  // Apollo useMutation
  const [uploadFilesMutation] = useMutation(UPLOAD_FILES);

  // Initialize roles once
  useEffect(() => {
    initializeRoles(previews);
  }, []);

  // ----------- (1) Roles handling -----------
  function initializeRoles(files: FileAttachment[]) {
    const initialRoles: { [role: string]: number } = {};
    files.forEach((file, i) => {
      if (file.roles) {
        for (const [role, hasRole] of Object.entries(file.roles)) {
          if (hasRole) {
            initialRoles[role] = i;
          }
        }
      }
    });

    // If no roles are set, assign everything to the first file
    if (Object.keys(initialRoles).length === 0 && files.length > 0) {
      initialRoles.Base = 0;
      initialRoles.Small = 0;
      initialRoles.Thumbnail = 0;
      initialRoles.OG_Image = 0;
      initialRoles.Swatch = 0;
    }
    setImageRoles(initialRoles);

    setPreviews((prev) =>
      prev.map((file, i) => {
        const newRoles = Object.keys(initialRoles).reduce(
          (acc, role) => {
            acc[role] = initialRoles[role] === i;
            return acc;
          },
          {} as { [role: string]: boolean }
        );
        return { ...file, roles: newRoles };
      })
    );
  }

  // ----------- (2) Remove file -----------
  function handleRemoveFile(index: number) {
    setPreviews((prevPreviews) => {
      const updatedPreviews = prevPreviews.filter((_, i) => i !== index);
      if (updatedPreviews.length > 0) {
        const removedFile = prevPreviews[index];
        if (removedFile?.roles) {
          for (const role of Object.keys(removedFile.roles)) {
            if (removedFile.roles[role]) {
              updatedPreviews[0].roles![role] = true;
            }
          }
        }
      }
      setImageRoles((prevRoles) => {
        const newRoles = { ...prevRoles };
        for (const role of Object.keys(newRoles)) {
          if (newRoles[role] === index) {
            delete newRoles[role];
            if (updatedPreviews.length > 0) {
              newRoles[role] = 0;
            }
          } else if (newRoles[role] > index) {
            newRoles[role] -= 1;
          }
        }
        return newRoles;
      });
      return updatedPreviews;
    });

    if (onFileRemove) {
      onFileRemove(previews[index].name);
    }
  }

  // ----------- (3) Drag & drop reorder -----------
  function moveImage(fromIndex: number, toIndex: number) {
    setPreviews((prev) => {
      const cloned = [...prev];
      const [moved] = cloned.splice(fromIndex, 1);
      cloned.splice(toIndex, 0, moved);

      setImageRoles((prevRoles) => {
        const newRoles = { ...prevRoles };
        for (const role of Object.keys(newRoles)) {
          if (newRoles[role] === fromIndex) {
            newRoles[role] = toIndex;
          } else if (newRoles[role] > fromIndex && newRoles[role] <= toIndex) {
            newRoles[role] -= 1;
          } else if (newRoles[role] < fromIndex && newRoles[role] >= toIndex) {
            newRoles[role] += 1;
          }
        }
        return newRoles;
      });

      initializeRoles(cloned);
      if (onFileIndexChange) onFileIndexChange(fromIndex, toIndex);
      return cloned;
    });
  }

  // ----------- (4) Changing roles -----------
  function handleRoleChange(
    newRole: string,
    oldRole: string,
    targetIndex: number
  ) {
    setImageRoles((prevRoles) => {
      const newRoles = { ...prevRoles };
      if (oldRole) delete newRoles[oldRole];
      if (newRole) newRoles[newRole] = targetIndex;

      const updatedFileData = {
        roles: Object.keys(newRoles).reduce(
          (acc, role) => {
            acc[role] = newRoles[role] === targetIndex;
            return acc;
          },
          {} as { [role: string]: boolean }
        ),
      };

      setPreviews((prev) => {
        const cloned = [...prev];
        cloned[targetIndex] = {
          ...cloned[targetIndex],
          roles: updatedFileData.roles,
        };
        // If you unassigned oldRole, ensure it's false
        if (oldRole && newRoles[oldRole] !== undefined) {
          const oldRoleIndex = newRoles[oldRole];
          if (cloned[oldRoleIndex]?.roles) {
            cloned[oldRoleIndex].roles![oldRole] = false;
          }
        }
        return cloned;
      });

      if (onFileUpdate) onFileUpdate(targetIndex, updatedFileData);
      return newRoles;
    });
  }

  // ----------- (5) Convert file to base64 -----------
  async function fileToBase64(file: File): Promise<string> {
    return new Promise((resolve, reject) => {
      const reader = new FileReader();
      reader.onload = () => {
        // e.g. data:image/png;base64,iVBORw0KGgoAAAANSUh...
        const result = reader.result as string;
        const base64String = result.split(",")[1];
        resolve(base64String);
      };
      reader.onerror = reject;
      reader.readAsDataURL(file);
    });
  }

  // ----------- (6) Calculate resolution -----------
  async function calculateImageResolution(file: File) {
    return new Promise<string>((resolve) => {
      const img = new Image();
      img.src = URL.createObjectURL(file);
      img.onload = () => {
        resolve(`${img.width}x${img.height} px`);
      };
    });
  }

  // ----------- (7) React Dropzone -----------
  const { getRootProps, getInputProps } = useDropzone({
    onDrop: async (
      acceptedFiles: File[],
      fileRejections: FileRejection[],
      event: DropEvent
    ) => {
      // (A) Show placeholders first
      const newPlaceholders: FileAttachment[] = acceptedFiles.map((f, i) => ({
        name: f.name,
        preview: "",
        size: Math.round(f.size / 1024),
        resolution: "",
        uploadProgress: 0,
        isUploading: true,
        roles:
          i === 0 && previews.length === 0
            ? {
                Base: true,
                Small: true,
                Thumbnail: true,
                OG_Image: true,
                Swatch: true,
              }
            : {
                Base: false,
                Small: false,
                Thumbnail: false,
                OG_Image: false,
                Swatch: false,
              },
      }));
      setPreviews((prev) => [...prev, ...newPlaceholders]);

      // (B) For each file: convert to base64 + mutate
      const uploadedResults: FileAttachment[] = [];
      for (let i = 0; i < acceptedFiles.length; i++) {
        const file = acceptedFiles[i];
        const finalUrl = "";
        let resolution = "";
        try {
          const base64Str = await fileToBase64(file); // Convert file
          resolution = await calculateImageResolution(file);

          // GraphQL call
          const response = await uploadFilesMutation({
            variables: {
              input: [
                {
                  name: file.name,
                  base64_encoded_file: base64Str, // EXACTLY as your postman example
                },
              ],
            },
          });
          const items = response?.data?.UploadFiles?.items || [];
          const finalUrl = items[0]?.full_path || "";
          let previewUrl = finalUrl; // Default to original          // Ensure full URL
          if (finalUrl) {
            previewUrl = finalUrl.replace(/^.*?pub\/media\//, ''); // Clean up path
            previewUrl = `${baseUrl}${previewUrl.startsWith('/') ? '' : '/'}${previewUrl}`;
          }
          uploadedResults.push({
            name: file.name,
            preview: previewUrl, // from server
            originalUrl: finalUrl,  // Keep the raw API path
            size: Math.round(file.size / 1024),
            resolution,
            uploadProgress: 100,
            isUploading: false,
            roles:
              i === 0 && previews.length === 0
                ? {
                  Base: true,
                  Small: true,
                  Thumbnail: true,
                  OG_Image: true,
                  Swatch: true,
                }
                : {
                  Base: false,
                  Small: false,
                  Thumbnail: false,
                  OG_Image: false,
                  Swatch: false,
                },
          });
          // Log the correct URL
        } catch (e) {
          console.error("File upload failed:", e);
        }      
      }

      // (C) Update final array (replacing placeholders)
      setPreviews((prev) => {
        // The first `previews.length` are your old items,
        // the next `acceptedFiles.length` are placeholders:
        const finalArray = [
          ...prev.slice(0, previews.length),
          ...uploadedResults,
        ];
        return finalArray;
      });

      // (D) Re-init roles so everything lines up
      initializeRoles([...previews, ...uploadedResults]);

      // (E) onDrop callback
      if (onDrop) onDrop(uploadedResults);
    },
    accept,
    ...reactDropZoneProps,
  });

  const imageOnly = useMemo(() => {
    return Boolean(accept && accept["image/*"]);
  }, [accept]);

  // ---------- (E) Render ----------
  return (
    <DndProvider backend={HTML5Backend}>
      {/* Show previews if images */}
      {imageOnly && !noPreview && previews.length > 0 && (
        <div className="d-flex flex-wrap gap-2 mb-2">
          {previews.map((file, i) => (
            <DraggableImage
              key={`${file.name}-${i}`}
              id={file.name}
              index={i}
              moveImage={moveImage}
            >
              <CustomImageAttachmentPreview
                image={file.preview}
                handleClose={() => handleRemoveFile(i)}
                isFirst={i === 0}
                roles={Object.keys(imageRoles).filter(
                  (role) => imageRoles[role] === i
                )}
                onRoleChange={(newRole, oldRole) =>
                  handleRoleChange(newRole, oldRole, i)
                }
                onFileUpdate={(updatedData) => {
                  setPreviews((prev) => {
                    const cloned = [...prev];
                    cloned[i] = { ...cloned[i], ...updatedData };
                    return cloned;
                  });
                  if (onFileUpdate) onFileUpdate(i, updatedData);
                }}
                fileData={file}
                index={i}
              />
            </DraggableImage>
          ))}
        </div>
      )}

      {/* Dropzone UI */}
      <div
        {...getRootProps()}
        className={classNames(className, "dropzone", {
          "dropzone-sm": size === "sm",
        })}
      >
        <input {...getInputProps()} />
        {children ? (
          <>{children}</>
        ) : (
          <div className="text-body-tertiary text-opacity-85 fw-bold fs-9">
            Drag your {imageOnly ? "photo" : "files"} here{" "}
            <span className="text-body-secondary">or </span>
            <Button variant="link" className="p-0">
              Browse from device
            </Button>
            <br />
            <img
              className="mt-3"
              src={imageIcon}
              width={size === "sm" ? 24 : 40}
              alt=""
            />
          </div>
        )}
      </div>
    </DndProvider>
  );
}
